/** @file validateDoc mesh rules: shared topology rule, skinned weights (≤ 4, sum 1), bind poses. */
import type { Doc } from './schema';
import type { MeshAtt } from './schemaParts/attachments';
import type { NeedRef } from './refChecker';
import { meshTopologyProblem, type MeshTopologyField } from './meshTopologyProblem';
import { makeError, type Pose2DError } from '../pose2dError';

type Skinned = Extract<MeshAtt['geometry'], { kind: 'skinned' }>;

/** Most bones one vertex may follow (spec §1). */
const MAX_INFLUENCES = 4;
/** How far the weights of one vertex may sum from 1. */
const SUM_TOLERANCE = 1e-6;
/** Where each topology field lives under `attachments.<id>` (stored positions are in `geometry`). */
const FIELD_PATH: Record<MeshTopologyField, string> = {
  uvs: 'uvs', vertices: 'geometry', triangles: 'triangles', hull: 'hull', edges: 'edges', mirrorVertexMap: 'mirrorVertexMap',
};

/** The first vertex that breaks one per-vertex rule, its offending value, and how many vertices break it. */
type Breach = { vertex: number; value: number; count: number };

/** Counts one more breach, keeping the first vertex and its value. */
function note(b: Breach, vertex: number, value: number): void {
  if (b.count === 0) { b.vertex = vertex; b.value = value; }
  b.count += 1;
}
const tally = (count: number): string => `${count} ${count === 1 ? 'vertex breaks' : 'vertices break'} this`;

/** True when the linear part of a bind pose `[a, b, c, d, tx, ty]` can be inverted (the compiler's inverseBindPoses test). */
const invertible = (m: readonly number[]): boolean => Number.isFinite(1 / ((m[0] ?? 0) * (m[3] ?? 0) - (m[1] ?? 0) * (m[2] ?? 0)));

/** One skinned mesh under check: its geometry path, a VALIDATION reporter whose hint names the mesh, bone names. */
type MeshCheck = { g: Skinned; at: string; need: NeedRef; fail: (path: string, hint: string) => void; bone: (id: string) => string };

/** One weighted bone, checked once per mesh: it exists and has an invertible bind pose. */
function checkWeightedBone(c: MeshCheck, bone: string): void {
  c.need(bone, 'bone', `${c.at}.weights`);
  const pose = Object.hasOwn(c.g.bindPose, bone) ? c.g.bindPose[bone] : undefined;
  if (pose === undefined) c.fail(`${c.at}.bindPose`, `weighted bone ${c.bone(bone)} has no bindPose entry`);
  else if (!invertible(pose)) c.fail(`${c.at}.bindPose.${bone}`, `the bind pose of bone ${c.bone(bone)} is singular`);
}

/** Per-vertex weight rules: at least one influence, at most four, summing to 1; each weighted bone checked once. */
function checkWeights(c: MeshCheck): void {
  const seen = new Set<string>();
  const many: Breach = { vertex: -1, value: 0, count: 0 };
  const off: Breach = { vertex: -1, value: 0, count: 0 };
  c.g.weights.forEach((vertex, v) => {
    if (vertex.length === 0) { c.fail(`${c.at}.weights.${v}`, `vertex ${v} needs at least one influence`); return; }
    let sum = 0;
    for (let k = 0; k < vertex.length; k++) {
      const influence = vertex[k];
      if (influence === undefined) continue;
      sum += influence[1];
      if (!seen.has(influence[0])) { seen.add(influence[0]); checkWeightedBone(c, influence[0]); }
    }
    if (vertex.length > MAX_INFLUENCES) note(many, v, vertex.length);
    if (!(Math.abs(sum - 1) <= SUM_TOLERANCE)) note(off, v, sum);
  });
  if (many.count > 0) c.fail(`${c.at}.weights.${many.vertex}`, `vertex ${many.vertex} has ${many.value} influences; at most ${MAX_INFLUENCES} per vertex (${tally(many.count)})`);
  if (off.count > 0) c.fail(`${c.at}.weights.${off.vertex}`, `the weights of vertex ${off.vertex} sum to ${off.value}; each vertex sums to 1 within ${SUM_TOLERANCE.toExponential()} (${tally(off.count)})`);
}

function checkSkinned(c: MeshCheck): void {
  if (c.g.weights.length * 2 !== c.g.bindVertices.length) c.fail(`${c.at}.weights`, `one weight list per vertex (${c.g.weights.length} lists for ${c.g.bindVertices.length / 2} vertices)`);
  checkWeights(c);
  for (const bone of Object.keys(c.g.bindPose)) c.need(bone, 'bone', `${c.at}.bindPose.${bone}`);
}

/**
 * Appends, per mesh, the first broken topology rule (meshTopologyProblem, at `attachments.<id>.<field>`) and the
 * skinned rules: one weight list per vertex, 1..4 influences summing to 1, every weighted bone with an invertible bind
 * pose. Hints name the mesh and bones by name; ids appear only in paths.
 */
export function checkDocMeshes(doc: Doc, need: NeedRef, out: Pose2DError[]): void {
  const bone = (id: string): string => doc.bones[id]?.name ?? id;
  for (const [id, att] of Object.entries(doc.attachments)) {
    if (att.type !== 'mesh') continue;
    const fail = (path: string, hint: string): void => { out.push(makeError('VALIDATION', {}, { path, hint: `mesh "${att.name}": ${hint}` })); };
    const g = att.geometry;
    const vertices = g.kind === 'rigid' ? g.vertices : g.bindVertices;
    const problem = meshTopologyProblem({ uvs: att.uvs, vertices, triangles: att.triangles, hull: att.hull, edges: att.edges, mirrorVertexMap: att.mirrorVertexMap });
    if (problem !== null) fail(`attachments.${id}.${FIELD_PATH[problem[0]]}`, problem[1]);
    if (g.kind === 'skinned') checkSkinned({ g, at: `attachments.${id}.geometry`, need, fail, bone });
  }
}
